/**
 * Shared geometry helpers for tools that persist hand-picked or
 * algorithmically-produced polylines as GeoJSON features: the Add Line /
 * Add Polygon drawing toolbars, and (from Task 10) the crest-line save
 * handler.
 *
 * `buildLineGeometry` and `projectPartsToWGS84` used to live duplicated in
 * `AddLineToolbar.tsx` and `AddPolygonToolbar.tsx` (the polygon copy also
 * closes each ring before building its geometry, which stays local to that
 * toolbar — only the LineString/MultiLineString shape is shared here).
 */
import { project } from '@/core';

/** A single vertex: project world coordinates, Z-up. */
export type Coord3 = [number, number, number];

/** One drawn/extracted part — a line, an unclosed ring, or similar. */
export type GeometryPart = Coord3[];

/** Build the geometry for persistence: LineString if 1 part, MultiLineString if more. */
export function buildLineGeometry(allParts: GeometryPart[], targetSrid: number) {
  if (allParts.length === 1) {
    return {
      type: 'LineString' as const,
      crs: {
        type: 'name' as const,
        properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
      },
      coordinates: allParts[0],
    };
  }
  return {
    type: 'MultiLineString' as const,
    crs: {
      type: 'name' as const,
      properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
    },
    coordinates: allParts,
  };
}

/**
 * Reproject every part to WGS84, part-by-part, preserving the caller's part
 * type (`LinePart[]`, `PolygonPart[]`, or a plain `GeometryPart[]`). Returns
 * `null` when there is no usable SRID, when any vertex fails to project, or
 * when `project()` throws (e.g. proj4 definitions not yet loaded).
 */
export async function projectPartsToWGS84<P extends GeometryPart>(
  allParts: P[],
  srid: number,
): Promise<P[] | null> {
  if (!srid || srid === -1) return null;
  try {
    const out: P[] = [];
    for (const part of allParts) {
      const projected = await Promise.all(part.map((c) => project(c, `EPSG:${srid}`)));
      if (projected.some((p) => !p)) return null;
      out.push(projected as P);
    }
    return out;
  } catch {
    return null;
  }
}
